/**
 * 初始布局：把器材摆到它所属的分区里。
 * 对应技术设计 §4.2（器材托盘固定在左侧，实验主区足够大）。
 *
 * 优先级：实验包显式声明的 pose > 分区自动排布。
 * 自动排布只决定**初始位置**，学生把器材从托盘拖到桌面是 P1 的事。
 */

import type { LabObject } from "../eom/lab-object.js";
import { getTemplate } from "../eom/equipment-registry.js";
import { findZone, type Zone } from "./zones.js";

const TRAY_PADDING = 14;
const TRAY_GAP = 18;
const BENCH_PADDING = 28;
const BENCH_GAP = 34;

interface PlacementRequest {
  object: LabObject;
  zoneId: string;
  /** 实验包是否显式给了坐标；给了就不自动排布 */
  pinned: boolean;
}

function sizeOf(object: LabObject): { width: number; height: number } {
  const template = getTemplate(object.type);
  return template
    ? { width: template.bounds.width, height: template.bounds.height }
    : { width: 60, height: 60 };
}

/** 托盘：单列竖排，从上往下；放不下就换列（托盘窄，通常一列够）。 */
function layoutTray(requests: PlacementRequest[], zone: Zone): void {
  let cursorY = zone.bounds.y + TRAY_PADDING;
  let columnX = zone.bounds.x + TRAY_PADDING;
  let columnWidth = 0;

  for (const request of requests) {
    const size = sizeOf(request.object);
    if (cursorY + size.height > zone.bounds.y + zone.bounds.height - TRAY_PADDING) {
      columnX += columnWidth + TRAY_GAP;
      cursorY = zone.bounds.y + TRAY_PADDING;
      columnWidth = 0;
    }
    request.object.pose = { ...request.object.pose, x: columnX, y: cursorY };
    cursorY += size.height + TRAY_GAP;
    columnWidth = Math.max(columnWidth, size.width);
  }
}

/** 实验桌：沿底部基线横排，高的器材（如铁架台）自然贴地，视觉上像摆在桌面上。 */
function layoutBench(requests: PlacementRequest[], zone: Zone): void {
  const baselineY = zone.bounds.y + zone.bounds.height - BENCH_PADDING;
  let cursorX = zone.bounds.x + BENCH_PADDING;

  for (const request of requests) {
    const size = sizeOf(request.object);
    request.object.pose = {
      ...request.object.pose,
      x: cursorX,
      y: Math.max(zone.bounds.y + BENCH_PADDING, baselineY - size.height),
    };
    cursorX += size.width + BENCH_GAP;
  }
}

/**
 * 落位分区：实验包声明 > 模板缺省 > workbench。
 * 缺省分区不在本场景启用时（如实验包没开 water_area），退回实验桌而不是退回托盘 ——
 * 退回托盘会让固定装置莫名其妙出现在器材栏里。
 */
function resolveZoneId(object: LabObject, declaredZone: string | undefined, zones: Zone[]): string {
  const candidate = declaredZone ?? getTemplate(object.type)?.defaultZone ?? "workbench";
  if (findZone(zones, candidate)) return candidate;
  if (findZone(zones, "workbench")) return "workbench";
  return zones[0]?.id ?? "workbench";
}

export interface LayoutInput {
  object: LabObject;
  /** 实验包 EquipmentSpec 里声明的 zone 与 pose */
  zone?: string;
  hasExplicitPose: boolean;
}

/**
 * 排布器材，返回**新数组**（元素为新对象，不改传入的 LabObject）。
 */
export function layoutObjects(inputs: LayoutInput[], zones: Zone[]): LabObject[] {
  const requests: PlacementRequest[] = inputs.map((input) => ({
    object: { ...input.object, pose: { ...input.object.pose } },
    zoneId: resolveZoneId(input.object, input.zone, zones),
    pinned: input.hasExplicitPose,
  }));

  for (const zone of zones) {
    const inZone = requests.filter((request) => !request.pinned && request.zoneId === zone.id);
    if (inZone.length === 0) continue;

    if (zone.kind === "tray") {
      layoutTray(inZone, zone);
    } else {
      layoutBench(inZone, zone);
    }
  }

  return requests.map((request) => request.object);
}
